import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useFocusEffect, useRouter, useGlobalSearchParams, type Href } from 'expo-router';
import { SquarePen } from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { TrackedLink as TrackedRouterLink } from './TrackedLink';
import { DirectoryListEmpty } from './directory/DirectoryListEmpty';
import { DirectoryCard } from './directory/directoryCard/DirectoryCard';
import { DirectoryModFilters } from './directory/directoryFilters';
import { DirectoryScene } from './directory/directoryScene';
import { FeaturedExpertsScene } from './directory/featuredExpertsScene';
import { FeaturedExpertsSection } from './directory/featuredExpertsSection';

import { useAnnouncementHeaderBanner } from '@/components/AnnouncementHeaderBanner';
import { InquiryDemoModal } from '@/components/InquiryDemoModal';
import { WhenToUseExplainer } from '@/components/MedicalOnDemand/WhenToUseExplainer';
import {
  buildOnboardingSteps,
  OnboardingTutorialModal,
} from '@/components/OnboardingTutorialModal';
import { ResultsAnnouncer, useResultsAnnouncement } from '@/components/ResultsAnnouncer';
import { SegmentTabs } from '@/components/SegmentTabs';
import { TabRootScenes, type TabRootScenesHandle } from '@/components/TabRootList';
import { Header } from '@/components/header';
import { TAB_HEADER_ITEMS } from '@/components/tabHeaderItems';
import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { FlatList } from '@/components/ui/flat-list';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { LinkText } from '@/components/ui/link';
import { NewBadge } from '@/components/ui/new-badge';
import { Pressable } from '@/components/ui/pressable';
import { RefreshControl } from '@/components/ui/refresh-control';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import {
  getProductSupportTeam,
  MOD_NEW_UNTIL,
  TREATMENT_SUPPORT_LABEL,
  WHEN_TO_USE_EXPLAINER,
} from '@/constants/medicalOnDemand';
import { NAV_LABELS } from '@/constants/navigation';
import { ROUTES } from '@/constants/routes';
import { useOrderedDirectoryProfiles } from '@/features/directory';
import {
  chooseCancerType,
  chooseProductType,
  getCancerParamAction,
  isTreatmentUnresolved,
  reconcileFilters,
  requiredCancerFromLookup,
  settleAgainstCancerTypes,
  type DirectoryItem,
  type DirectoryExpertItem,
  type DirectoryAnonymousItem,
  type DirectoryEmptyItem,
} from '@/features/directory/model';
import {
  directorySearchStore,
  retainDirectorySearch,
  useDirectorySearchState,
} from '@/features/directory/state';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useCollapsibleDirectoryIntro } from '@/hooks/useCollapsibleDirectoryIntro';
import { useColors } from '@/hooks/useColors';
import { useFeaturedExpertsIndicator } from '@/hooks/useFeaturedExpertsIndicator';
import { useFetchNextPageWhenIdle } from '@/hooks/useFetchNextPageWhenIdle';
import { useNewBadgePulse } from '@/hooks/useNewBadgePulse';
import { useOnboardingTutorialModal } from '@/hooks/useOnboardingTutorialModal';
import { useToaster } from '@/hooks/useToaster';
import { useWhenToUseExplainerEnabled } from '@/hooks/useWhenToUseExplainerCopy';
import {
  getAlgoliaExpertsInfiniteQueryOptions,
  getProductTypeById,
} from '@/services/getAlgoliaExperts';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import { getFeaturedExpertsV2QueryOptions } from '@/services/getFeaturedExpertsV2';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import { segmentClient } from '@/util/analytics';
import { getProfileFormalNameOrFallback } from '@/util/getProfileNameAndInitials';
import { isTestUser } from '@/util/isTestUser';
import { ExpertProfile } from '@/util/mapDirectoryProfiles';

const directoryTagline = 'Ask an expert your question.';

const directorySubtitleMobile =
  'Submit an inquiry about a patient case or a topic on your mind. Search by expert name, cancer type, or keyword.';

type DirectoryTabKey = 'directory' | 'featured';

type DirectoryTabRoute = {
  key: DirectoryTabKey;
  title: string;
};

const DIRECTORY_TAB_ROUTES: DirectoryTabRoute[] = [
  {
    key: 'directory',
    title: 'All Experts',
  },
  {
    key: 'featured',
    title: 'Featured',
  },
];

const directoryDescription =
  'Submit an inquiry about a patient case or a topic on your mind. Search by expert name, cancer type, or keyword.';

/**
 * Trigger pagination when user scrolls to 80% of content height.
 * Provides smooth infinite scroll by prefetching next page before reaching bottom.
 */
const PAGINATION_THRESHOLD = 0.8;

/**
 * Number of screen heights worth of content to keep in memory (5.5 screens above + 5.5 below).
 * Balances memory usage with scroll performance by rendering items within this window.
 */
const FLATLIST_WINDOW_SIZE = 11;

/**
 * Maximum number of items to render per frame during scrolling.
 * Prevents UI blocking by spreading rendering work across multiple frames.
 */
const FLATLIST_MAX_RENDER_BATCH = 10;

const cancerValueFor = (productTypeId: string) =>
  getProductTypeById(productTypeId)?.cancerFocusValue;

/** A treatment type from the address, or none when it names no real one. */
const validProductTypeId = (value: string | undefined) =>
  value && getProductTypeById(value) ? value : '';

/**
 * Empty set constant to avoid creating new Set instances on every render.
 */
const EMPTY_SET = new Set<string>();

/**
 * Directory Component
 *
 * Main expert directory page that displays searchable list of medical experts.
 * Includes new user demo modal, filtering, search, and infinite scroll functionality.
 *
 * Features:
 * - Expert search and filtering by cancer type, expertise, location
 * - Infinite scroll with pagination via Algolia
 * - Onboarding tutorial modal (auto-shows for new users within 1 month window)
 * - Anonymous inquiry option
 * - Responsive grid layout (1-3 columns based on screen size)
 * - Announcements integration
 * - Analytics tracking for search and profile interactions
 *
 * State Management:
 * - Search query and input states with debounced search
 * - Cancer type filter with URL parameter support
 * - Onboarding tutorial modal visibility based on user account age and storage
 * - Refresh state for pull-to-refresh functionality
 *
 * Modal Behavior:
 * - OnboardingTutorialModal appears automatically for qualifying new users
 * - Checks eligibility via useOnboardingTutorialModal hook
 * - Integrates with user data from getCurrentUserV2QueryOptions
 *
 * User Group Behavior:
 * - Primum users: See all experts (regular + test) in filtered results via infinite scroll
 * - Regular users: Only see non-test experts in filtered results
 *
 * Anonymous Expert Display:
 * - Anonymous profile shows focus area with icon in place of title
 * - Uses ANONYMOUS_EXPERT constants for directory card description and focus areas
 *
 * Layout — which structure is used:
 * - Split: When there is product support, active filters, and multi-column, show product support
 *   cards in the left column and the expert list in the right column.
 * - Featured: When there are featured experts, multi-column, and no active filters, show all
 *   experts on the left and the featured section on the right.
 * - Mobile: Single column shows the Directory or the Featured list, picked by a segment
 *   control at the top of the list so the native large title can collapse against it.
 * - Default: A single FlatList shows all directory items.
 *
 * @param withOnboardingTutorial Mount the onboarding tutorial from this screen.
 *   Off by default and set only by the authenticated route, because this
 *   component backs two of them: the public directory and the tab. During
 *   sign-up a user renders the public one first — the redirect to the tab
 *   waits on an effect that returns early while the user id is resolving — so
 *   mounting the tutorial unconditionally opened it once per route, each
 *   instance holding its own state and running its own eligibility check.
 */
export default function Directory({
  withOnboardingTutorial = false,
}: {
  withOnboardingTutorial?: boolean;
}) {
  const toast = useToaster();

  const { currentUser, needsNpiVerification } = useAuthStatus();

  const { isPrimumUser } = useCognitoGroups();

  const { bottom } = useSafeAreaInsets();

  const searchQuery = useDirectorySearchState((state) => state.searchQuery);

  /**
   * The search store outlives this screen, so the last directory screen to leave takes the text
   * with it, as it did when the text was local state.
   */
  useEffect(() => retainDirectorySearch(), []);

  const params = useGlobalSearchParams<{ cancerId?: string; productTypeId?: string }>();

  const cancerFocusQuery = useQuery(getCancerTypesV2QueryOptions());

  const cancerTypeLookup = cancerFocusQuery.data?.cancerTypeLookup;

  const requiredCancerFor = useMemo(
    () => requiredCancerFromLookup(cancerTypeLookup, cancerValueFor),
    [cancerTypeLookup]
  );

  /**
   * Restored from the URL, but a treatment type only when the id resolves to a real one. An
   * unrecognised value would otherwise filter the directory down to nothing and look like an
   * outage rather than a bad link, so a stale or hand-edited param degrades to no filter
   * instead. The pair is settled together so a treatment that arrived alone still shows the
   * cancer type it belongs to.
   */
  const [initialFilters] = useState(() =>
    reconcileFilters(
      {
        focusId: params.cancerId || '',
        productTypeId: validProductTypeId(params.productTypeId),
      },
      requiredCancerFor
    )
  );

  const [focusId, setFocusId] = useState(initialFilters.focusId);

  const flatListRef = useRef<FlatList>(null);

  const tabRootRef = useRef<TabRootScenesHandle>(null);

  /**
   * The cancer id last read from or written to the URL, which is what tells a link apart
   * from the directory's own writes. See `getCancerParamAction`.
   */
  const lastSyncedCancerIdRef = useRef(params.cancerId ?? '');

  const supersededCancerIdRef = useRef('');

  /** Set when a link changed the filter, so the reader is told once the label is known. */
  const [linkAppliedCancerId, setLinkAppliedCancerId] = useState('');

  // Ref pattern allows buildInquiryUrl to access current focusId without recreating
  // the callback on every focusId change. This prevents unnecessary re-renders of
  // memoized directory cards while ensuring the URL always uses the latest focusId.
  const focusIdRef = useRef(focusId);

  focusIdRef.current = focusId;

  const [productTypeId, setProductTypeId] = useState(initialFilters.productTypeId);

  const [restoreFocusId, setRestoreFocusId] = useState(initialFilters.restoreFocusId);

  const selectCancerType = useCallback(
    (nextFocusId: string) => {
      const next = chooseCancerType(
        { focusId, productTypeId, restoreFocusId },
        nextFocusId,
        requiredCancerFor
      );

      setFocusId(next.focusId);

      setProductTypeId(next.productTypeId);

      setRestoreFocusId(next.restoreFocusId);
    },
    [focusId, productTypeId, restoreFocusId, requiredCancerFor]
  );

  const selectProductType = useCallback(
    (nextProductTypeId: string) => {
      const next = chooseProductType(
        { focusId, productTypeId, restoreFocusId },
        nextProductTypeId,
        requiredCancerFor
      );

      setFocusId(next.focusId);

      setProductTypeId(next.productTypeId);

      setRestoreFocusId(next.restoreFocusId);
    },
    [focusId, productTypeId, restoreFocusId, requiredCancerFor]
  );

  const settledAgainstCancerTypesRef = useRef(
    !!cancerTypeLookup || !validProductTypeId(params.productTypeId)
  );

  /**
   * A pair from the address read before the cancer type list loaded could not yet name a
   * treatment's cancer type. Settle it when the list arrives, so a treatment that came alone shows
   * its cancer type and one that conflicts with the link's cancer type is dropped. It stays
   * unsettled until the list can name the treatment's cancer type, so a list that lacked it is
   * tried again when a refetch replaces it.
   */
  useEffect(() => {
    if (!cancerTypeLookup || settledAgainstCancerTypesRef.current) {
      return;
    }

    const settled = settleAgainstCancerTypes(
      { focusId, productTypeId, restoreFocusId },
      requiredCancerFor
    );

    if (!settled) {
      return;
    }

    settledAgainstCancerTypesRef.current = true;

    setFocusId(settled.focusId);

    setProductTypeId(settled.productTypeId);

    setRestoreFocusId(settled.restoreFocusId);
  }, [cancerTypeLookup, focusId, productTypeId, restoreFocusId, requiredCancerFor]);

  const numColumns =
    useBreakpointValue({
      default: 1,
      md: 2,
      lg: 3,
    }) || 1;

  const [refreshing, setRefreshing] = useState(false);

  const [showNewUserDemoModal, setShowNewUserDemoModal] = useState(false);

  const [showDemoModalManual, setShowDemoModalManual] = useState(false);

  const [isTreatmentSupportExplainerOpen, setIsTreatmentSupportExplainerOpen] = useState(false);

  /**
   * The section carries the marker while the window is open. The date closes it
   * for everyone at once, so it describes the feature rather than the reader's
   * relationship to it and nobody has to dismiss it.
   */
  const isExplainerEnabled = useWhenToUseExplainerEnabled();

  const showTreatmentSupportNewBadge = isExplainerEnabled && Date.now() < MOD_NEW_UNTIL.getTime();

  const [mobileTabIndex, setMobileTabIndex] = useState(0);

  const handleTabIndexChange = useCallback(
    (index: number) => {
      setMobileTabIndex(index);

      const selectedRoute = DIRECTORY_TAB_ROUTES[index];

      if (selectedRoute?.key === 'featured') {
        segmentClient.track(ANALYTICS_EVENTS.DIRECTORY_FEATURED_TAB_SELECTED);
      }
    },
    [setMobileTabIndex]
  );

  const resetDirectorySearch = useCallback(() => {
    directorySearchStore.getState().reset();

    setFocusId('');

    setProductTypeId('');

    setRestoreFocusId(undefined);
  }, []);

  const { setParams, navigate } = useRouter();

  /**
   * Both filters are written back to the URL together, so a reload or a shared
   * link comes back to the same view.
   *
   * Written together rather than in two effects because `setParams` takes the
   * whole set: writing one alone would drop the other. Both are optional, so an
   * existing link carrying only `cancerId` still behaves exactly as it did.
   */
  useFocusEffect(
    useCallback(() => {
      const paramCancerId = params.cancerId ?? '';

      const paramProductTypeId = params.productTypeId ?? '';

      const action = getCancerParamAction({
        paramCancerId,
        lastSyncedCancerId: lastSyncedCancerIdRef.current,
        supersededCancerId: supersededCancerIdRef.current,
        focusId,
        productMismatch: productTypeId !== paramProductTypeId,
      });

      if (paramCancerId === lastSyncedCancerIdRef.current) {
        supersededCancerIdRef.current = '';
      }

      if (action === 'adopt') {
        if (!cancerTypeLookup) {
          return;
        }

        lastSyncedCancerIdRef.current = paramCancerId;

        if (!cancerTypeLookup.has(Number(paramCancerId))) {
          setParams({ cancerId: focusId || undefined });

          return;
        }

        const linked = reconcileFilters(
          {
            focusId: paramCancerId,
            productTypeId: validProductTypeId(params.productTypeId) || productTypeId,
          },
          requiredCancerFor
        );

        setProductTypeId(linked.productTypeId);

        setFocusId(linked.focusId);

        setRestoreFocusId(linked.restoreFocusId);

        setMobileTabIndex(0);

        setLinkAppliedCancerId(paramCancerId);

        if (tabRootRef.current) {
          tabRootRef.current.scrollToTop();
        } else {
          flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
        }

        return;
      }

      if (action === 'write') {
        supersededCancerIdRef.current = paramCancerId;

        lastSyncedCancerIdRef.current = focusId;

        setParams({
          cancerId: focusId || undefined,
          productTypeId: productTypeId || undefined,
        });

        return;
      }

      if (paramCancerId && paramCancerId === focusId) {
        lastSyncedCancerIdRef.current = paramCancerId;
      }
    }, [
      focusId,
      productTypeId,
      params.cancerId,
      params.productTypeId,
      cancerTypeLookup,
      requiredCancerFor,
      setParams,
    ])
  );

  const expertsQuery = useInfiniteQuery({
    ...getAlgoliaExpertsInfiniteQueryOptions({
      searchQuery,
      focusId: focusId || requiredCancerFor(productTypeId) || '',
      productTypeId,
    }),
    enabled: !productTypeId || !!requiredCancerFor(productTypeId),
  });

  const buildInquiryUrl = useCallback((expertId: string | number) => {
    const params = new URLSearchParams();

    if (focusIdRef.current) {
      params.append('focusId', focusIdRef.current);
    }

    const queryString = params.toString();

    return `/directory/inquiry/${expertId}${queryString ? `?${queryString}` : ''}` as Href;
  }, []);

  useEffect(() => {
    if (expertsQuery.isError) {
      console.error('Error fetching experts: ', expertsQuery.error);

      segmentClient.track(ANALYTICS_EVENTS.SEARCH_FAILED, {
        error: expertsQuery.error ? JSON.stringify(expertsQuery.error) : 'Unknown error',
      });

      toast.showError({ title: 'Unable to submit search' });
    }
  }, [expertsQuery.isError, expertsQuery.error]);

  const { shouldShow, markShown, markShowLater } = useOnboardingTutorialModal(
    currentUser?.id?.toString()
  );

  /**
   * The empty fallback is deliberate: it reads as "no name available" and keeps
   * the unpersonalized title. A stand-in like "there" would greet the reader by
   * something that is not their name.
   */
  const onboardingSteps = useMemo(
    () => buildOnboardingSteps(getProfileFormalNameOrFallback(currentUser, '')),
    [currentUser]
  );

  useEffect(() => {
    const checkShowDemoModal = async () => {
      if (withOnboardingTutorial && currentUser?.id) {
        const shouldShowResult = await shouldShow(currentUser, needsNpiVerification);

        setShowNewUserDemoModal(shouldShowResult);
      }
    };

    checkShowDemoModal();
  }, [withOnboardingTutorial, currentUser?.id, currentUser, needsNpiVerification, shouldShow]);

  const profiles = useMemo(
    () => expertsQuery.data?.pages.flatMap((page) => page.hits) || [],
    [expertsQuery.data?.pages]
  );

  const filteredProfiles = useMemo(() => {
    if (isPrimumUser) {
      return profiles;
    }

    return profiles.filter((p) => !isTestUser(p));
  }, [profiles, isPrimumUser]);

  const featuredExpertsQuery = useQuery(getFeaturedExpertsV2QueryOptions());

  const featuredExperts = featuredExpertsQuery.data ?? [];

  const { hasNewExperts, markAsViewed: markFeaturedExpertsViewed } =
    useFeaturedExpertsIndicator(featuredExperts);

  useEffect(() => {
    const featuredTabIndex = DIRECTORY_TAB_ROUTES.findIndex((route) => route.key === 'featured');

    if (mobileTabIndex === featuredTabIndex && featuredExperts.length > 0) {
      markFeaturedExpertsViewed();
    }
  }, [mobileTabIndex, featuredExperts.length, markFeaturedExpertsViewed]);

  const featuredExpertIds = useMemo(() => {
    if (!featuredExperts.length) {
      return EMPTY_SET;
    }

    return new Set(featuredExperts.map((item) => String(item.expert.id)));
  }, [featuredExperts]);

  const selectedCancerTypeLabel = useMemo(() => {
    if (!focusId || !cancerFocusQuery.data) {
      return '';
    }

    const selectedCancerType = cancerFocusQuery.data.cancerTypeLookup.get(Number(focusId));

    if (!selectedCancerType) {
      return '';
    }

    return selectedCancerType.label;
  }, [focusId, cancerFocusQuery.data]);

  /**
   * Says what a link just filtered the directory to, so a press on a banner is not a
   * silent change of the list.
   */
  /**
   * A cancer id the lookup does not know would filter everything out and read as an outage,
   * and a fresh mount takes its first id straight from the URL, so a bad link has to be
   * dropped here as well as at adoption.
   */
  useEffect(() => {
    if (focusId && cancerTypeLookup && !cancerTypeLookup.has(Number(focusId))) {
      setFocusId('');
    }
  }, [focusId, cancerTypeLookup]);

  useEffect(() => {
    if (!linkAppliedCancerId || !cancerTypeLookup) {
      return;
    }

    setLinkAppliedCancerId('');

    const appliedLabel = cancerTypeLookup.get(Number(linkAppliedCancerId))?.label;

    if (appliedLabel) {
      toast.showInfo({ title: `Showing ${appliedLabel} experts` });
    }
  }, [linkAppliedCancerId, cancerTypeLookup, toast]);

  const selectedProductTypeLabel = useMemo(() => {
    if (!productTypeId) {
      return '';
    }

    const selectedProductType = getProductTypeById(productTypeId);

    return selectedProductType?.label || '';
  }, [productTypeId]);

  const isLoading = cancerFocusQuery.isFetching || expertsQuery.isFetching;

  const isFetchingNextPage = expertsQuery.isFetchingNextPage;

  const { colors } = useColors();

  const { onIntroLayout, introStyle, bandStyle, handleScroll } = useCollapsibleDirectoryIntro();

  const isEmpty = filteredProfiles.length === 0;

  const filterProps = useMemo(
    () => ({
      focusId,
      setFocusId: selectCancerType,
      selectedCancerTypeLabel,
      cancerTypeLookup: cancerFocusQuery.data?.cancerTypeLookup,
      colors,
      bottom,
    }),
    [
      focusId,
      selectCancerType,
      selectedCancerTypeLabel,
      cancerFocusQuery.data?.cancerTypeLookup,
      colors,
      bottom,
    ]
  );

  const data = useOrderedDirectoryProfiles(
    filteredProfiles,
    searchQuery,
    focusId,
    productTypeId,
    numColumns
  );

  const { productSupportProfiles, expertProfiles } = useMemo(() => {
    const productSupport: ExpertProfile[] = [];

    const experts: ExpertProfile[] = [];

    for (const item of data) {
      if (item.type === 'product') {
        productSupport.push(item.data);
      } else if (item.type === 'expert') {
        experts.push(item.data);
      }
    }

    return { productSupportProfiles: productSupport, expertProfiles: experts };
  }, [data]);

  const hasProductSupportItem = productSupportProfiles.length > 0;

  /**
   * The experts only: product support cards are not search hits. The list loads in pages, so
   * the count says "or more" while another page exists.
   */
  const resultsAnnouncement = useResultsAnnouncement({
    queryKey: JSON.stringify([searchQuery, focusId, productTypeId]),
    isSettled: expertsQuery.isSuccess && !expertsQuery.isFetching,
    isError: expertsQuery.isError,
    resultCount: expertProfiles.length,
    hasMore: expertsQuery.hasNextPage,
  });

  /**
   * Keyed on the treatment the section is showing, so choosing a different
   * filter flaps the marker again. Arriving at Bispecifics Antibodies by
   * filtering is the same moment as arriving at it by scrolling, and it used to
   * be the one moment the animation missed: the badge had already played on
   * mount, against whatever was on screen before.
   */
  const treatmentSupportBadgePulse = useNewBadgePulse({
    replayKey: String(productSupportProfiles[0]?.id ?? ''),
  });

  const hasActiveFilters = searchQuery || focusId || productTypeId;

  const useSplitLayout = hasProductSupportItem && numColumns > 1 && hasActiveFilters;

  const expertColumnsCount = numColumns === 3 ? 2 : 1;

  const expertDirectoryItems: DirectoryItem[] = useMemo(() => {
    const items = data.filter(
      (item): item is DirectoryExpertItem | DirectoryAnonymousItem =>
        item.type === 'expert' || item.type === 'anonymous'
    );

    const remainder = items.length % expertColumnsCount;

    if (expertColumnsCount > 1 && remainder > 0) {
      const fillers: DirectoryEmptyItem[] = Array.from(
        { length: expertColumnsCount - remainder },
        (_, i) => ({ type: 'empty', id: `expert-empty-${i}` })
      );

      return [...items, ...fillers];
    }

    return items;
  }, [data, expertColumnsCount]);

  const renderDirectoryItem = useCallback(
    ({ item }: { item: DirectoryItem }) => (
      <DirectoryCard
        item={item}
        isMultiColumn={numColumns > 1}
        buildInquiryUrl={buildInquiryUrl}
        featuredExpertIds={featuredExpertIds}
        /*
         * This list is the layout with no Treatment Support section, so the
         * card carries the marker and the explainer link here. In the split
         * layout below they sit on the section heading instead, and that call
         * site leaves this false so exactly one of the two renders them.
         */
        showExplainerAffordances={isExplainerEnabled && !useSplitLayout}
      />
    ),
    [numColumns, buildInquiryUrl, featuredExpertIds, useSplitLayout, isExplainerEnabled]
  );

  const directoryKeyExtractor = useCallback(
    (item: DirectoryItem, index: number) =>
      item.type === 'empty' ? item.id : (item.data.id?.toString() ?? `item-${index}`),
    []
  );

  const { refetch: refetchExperts } = expertsQuery;

  const fetchNextDirectoryPage = useFetchNextPageWhenIdle(expertsQuery);

  const { refetch: refetchCancerTypes } = cancerFocusQuery;

  /**
   * A treatment whose cancer type the loaded list cannot name has nothing to search with, and
   * a result list would be a guess. Say the search is unavailable rather than show no experts,
   * and retry by asking for the list again.
   */
  const treatmentUnresolved = isTreatmentUnresolved(
    productTypeId,
    requiredCancerFor,
    !cancerFocusQuery.isPending && !cancerFocusQuery.isFetching
  );

  const searchUnavailable = expertsQuery.isError || treatmentUnresolved;

  const retryDirectorySearch = useCallback(() => {
    if (treatmentUnresolved) {
      refetchCancerTypes().catch(() => undefined);

      return;
    }

    refetchExperts().catch(() => undefined);
  }, [refetchExperts, refetchCancerTypes, treatmentUnresolved]);

  const renderEmptyState = (columns: number, showNoMatches = true) => (
    <DirectoryListEmpty
      isLoading={isLoading}
      isError={searchUnavailable}
      onRetry={retryDirectorySearch}
      onResetSearch={resetDirectorySearch}
      numColumns={columns}
      showNoMatches={showNoMatches}
    />
  );

  const refreshDirectory = useCallback(async () => {
    setRefreshing(true);

    try {
      await refetchExperts();
    } finally {
      setRefreshing(false);
    }
  }, [refetchExperts]);

  const hasFeaturedExperts = featuredExperts.length > 0;

  const shouldShowFeatured = hasFeaturedExperts && !featuredExpertsQuery.isError;

  const useFeaturedLayout = shouldShowFeatured && numColumns > 1 && !hasActiveFilters;

  const useMobileTabLayout = numColumns === 1;

  const announcementBanner = useAnnouncementHeaderBanner();

  const renderDirectoryMainContent = () => {
    if (useSplitLayout) {
      return (
        <HStack className="flex-1 pt-8">
          <ScrollView
            className="flex-1"
            contentContainerClassName="gap-3 pb-10"
            showsVerticalScrollIndicator={false}>
            {/*
              The marker and the explainer link sit on the section, not on the
              card inside it.

              On the card they were buried: a reader met them after the card's
              own title and badge, competing with its content. Here they are the
              first thing in the column.

              The link also scopes correctly here. It explains a choice between
              this section and the experts column beside it, and putting it
              inside one of the options meant a reader looking at a single card
              met a link that was not about that card.
            */}
            <VStack className="mb-1 gap-1">
              <HStack className="items-center gap-2">
                <Heading size="lg">Related {TREATMENT_SUPPORT_LABEL}</Heading>
                {showTreatmentSupportNewBadge ? (
                  <Animated.View
                    ref={treatmentSupportBadgePulse.ref}
                    style={treatmentSupportBadgePulse.style}>
                    <NewBadge surface="card" />
                  </Animated.View>
                ) : null}
              </HStack>
              {/*
                The count and the link share a row rather than stacking.
                Stacked, the header ran to three lines against the experts
                column's two, so the two columns' first cards no longer started
                level, and the link sat alone directly above the card where it
                re-attached to the card we had just moved it off.
              */}
              {/*
                Baseline, not center. The count is 16px and the link 14px, so
                centring their boxes leaves the two baselines about a pixel
                apart, which reads as a wobble on a single line of text.
              */}
              <HStack className="flex-wrap items-baseline gap-x-3 gap-y-1">
                <Text className="text-typography-600">
                  {productSupportProfiles.length}{' '}
                  {productSupportProfiles.length === 1 ? 'Treatment' : 'Treatments'}
                </Text>
                {isExplainerEnabled ? (
                  <Pressable
                    className="web:cursor-pointer"
                    hitSlop={ICON_BUTTON_HIT_SLOP}
                    onPress={() => setIsTreatmentSupportExplainerOpen(true)}
                    {...getAccessibilityProps({
                      label: WHEN_TO_USE_EXPLAINER.linkLabel,
                      role: 'button',
                      hint: 'Opens a guide to choosing between a Primum expert and the manufacturer',
                    })}>
                    <Text size="sm" className="font-medium text-primary-600 underline">
                      {WHEN_TO_USE_EXPLAINER.linkLabel}
                    </Text>
                  </Pressable>
                ) : null}
              </HStack>
            </VStack>

            {productSupportProfiles.map((profile) => (
              <DirectoryCard
                key={profile.id}
                item={{ type: 'product', data: profile }}
                isMultiColumn
                buildInquiryUrl={buildInquiryUrl}
                featuredExpertIds={featuredExpertIds}
              />
            ))}
          </ScrollView>

          <Divider orientation="vertical" className="mx-3" />

          <FlatList
            className="flex-[2]"
            contentContainerClassName="gap-3 pb-10"
            key={`split-${expertColumnsCount}`}
            numColumns={expertColumnsCount}
            columnWrapperClassName={expertColumnsCount > 1 ? 'gap-3' : undefined}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            showsVerticalScrollIndicator={false}
            data={expertDirectoryItems}
            keyExtractor={directoryKeyExtractor}
            removeClippedSubviews={!isWeb}
            maxToRenderPerBatch={FLATLIST_MAX_RENDER_BATCH}
            windowSize={FLATLIST_WINDOW_SIZE}
            renderItem={renderDirectoryItem}
            onEndReachedThreshold={PAGINATION_THRESHOLD}
            onEndReached={fetchNextDirectoryPage}
            ListEmptyComponent={renderEmptyState(expertColumnsCount)}
            ListHeaderComponent={
              <VStack className="mb-1">
                <Heading size="lg">Multiple Myeloma Experts</Heading>
                <Text className="text-typography-600">
                  {expertProfiles.length} {expertProfiles.length === 1 ? 'Expert' : 'Experts'}
                </Text>
              </VStack>
            }
            ListFooterComponent={
              isFetchingNextPage ? (
                <VStack className="mb-4 mt-4">
                  <Spinner size="large" />
                </VStack>
              ) : null
            }
          />
        </HStack>
      );
    }

    if (useFeaturedLayout) {
      return (
        <HStack className="flex-1 pt-8">
          <FlatList
            className="flex-[2]"
            contentContainerClassName="gap-3 pb-10"
            key={`featured-${expertColumnsCount}`}
            numColumns={expertColumnsCount}
            columnWrapperClassName={expertColumnsCount > 1 ? 'gap-3' : undefined}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            showsVerticalScrollIndicator={false}
            data={data}
            keyExtractor={directoryKeyExtractor}
            removeClippedSubviews={!isWeb}
            maxToRenderPerBatch={FLATLIST_MAX_RENDER_BATCH}
            windowSize={FLATLIST_WINDOW_SIZE}
            renderItem={renderDirectoryItem}
            onEndReachedThreshold={PAGINATION_THRESHOLD}
            onEndReached={fetchNextDirectoryPage}
            ListEmptyComponent={renderEmptyState(expertColumnsCount, false)}
            ListHeaderComponent={
              <Heading size="lg" className="mb-1">
                All Experts
              </Heading>
            }
            ListFooterComponent={
              isFetchingNextPage ? (
                <VStack className="mb-4 mt-4">
                  <Spinner size="large" />
                </VStack>
              ) : null
            }
          />

          <Divider orientation="vertical" className="mx-3" />

          <ScrollView
            className="flex-1"
            contentContainerClassName="pb-10"
            showsVerticalScrollIndicator={false}>
            <FeaturedExpertsSection
              featuredExperts={featuredExperts}
              isLoading={featuredExpertsQuery.isLoading}
            />
          </ScrollView>
        </HStack>
      );
    }

    if (useMobileTabLayout) {
      return (
        <TabRootScenes
          ref={tabRootRef}
          sceneKeys={DIRECTORY_TAB_ROUTES.map((route) => route.key)}
          selectedKey={DIRECTORY_TAB_ROUTES[mobileTabIndex]?.key ?? 'directory'}
          lead={
            <>
              {announcementBanner}

              <SegmentTabs
                tabs={DIRECTORY_TAB_ROUTES.map((route) => ({
                  ...route,
                  hasNewContent: route.key === 'featured' && hasNewExperts,
                }))}
                selectedKey={DIRECTORY_TAB_ROUTES[mobileTabIndex]?.key ?? 'directory'}
                onSelect={(key) =>
                  handleTabIndexChange(DIRECTORY_TAB_ROUTES.findIndex((route) => route.key === key))
                }
              />
            </>
          }
          renderScene={(key) => {
            if (key === 'directory') {
              return (
                <DirectoryScene
                  listRef={flatListRef}
                  data={data}
                  isLoading={isLoading}
                  isError={searchUnavailable}
                  onRetry={retryDirectorySearch}
                  isFetchingNextPage={isFetchingNextPage}
                  refreshing={refreshing}
                  featuredExpertIds={featuredExpertIds}
                  buildInquiryUrl={buildInquiryUrl}
                  onRefresh={refreshDirectory}
                  onEndReached={fetchNextDirectoryPage}
                  onShowDemoModal={() => setShowDemoModalManual(true)}
                  onResetSearch={resetDirectorySearch}
                  filterProps={filterProps}
                  productTypeId={productTypeId}
                  setProductTypeId={selectProductType}
                  selectedProductTypeLabel={selectedProductTypeLabel}
                />
              );
            }

            return (
              <FeaturedExpertsScene
                featuredExperts={featuredExperts}
                isLoading={featuredExpertsQuery.isLoading}
                isError={featuredExpertsQuery.isError}
                refreshing={refreshing}
                onRefresh={async () => {
                  setRefreshing(true);

                  try {
                    await featuredExpertsQuery.refetch();
                  } finally {
                    setRefreshing(false);
                  }
                }}
                onBrowseAllExperts={() => {
                  const directoryTabIndex = DIRECTORY_TAB_ROUTES.findIndex(
                    (route) => route.key === 'directory'
                  );

                  setMobileTabIndex(directoryTabIndex);
                }}
              />
            );
          }}
        />
      );
    }

    return (
      <FlatList
        ref={flatListRef}
        className="flex-1"
        contentContainerClassName={twJoin(
          'gap-3 pb-10',
          numColumns > 1 && (!isEmpty || isLoading) ? 'mt-10' : 'mt-0',
          isEmpty && 'flex-1'
        )}
        key={numColumns}
        numColumns={isWeb ? numColumns : 1}
        keyboardDismissMode="on-drag"
        onScroll={handleScroll}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        columnWrapperClassName={numColumns > 1 && isWeb ? 'gap-3' : ''}
        stickyHeaderHiddenOnScroll
        stickyHeaderIndices={numColumns > 1 ? undefined : [0]}
        refreshControl={
          !isWeb ? (
            <RefreshControl refreshing={refreshing} onRefresh={refreshDirectory} />
          ) : undefined
        }
        ListHeaderComponent={
          numColumns > 1 ? null : (
            <VStack className="bg-background-0 pb-5 pt-4 md:hidden md:pt-0" space="lg">
              <VStack>
                <HStack className="flex-wrap items-baseline gap-1">
                  <Text className="text-md leading-normal text-typography-700">
                    {directorySubtitleMobile}
                  </Text>
                  <Button
                    onPress={() => {
                      segmentClient.track(ANALYTICS_EVENTS.INQUIRY_DEMO_OPENED, {
                        trigger_type: 'demo_link',
                      });

                      setShowDemoModalManual(true);
                    }}
                    variant="link"
                    size="sm"
                    action="secondary">
                    <ButtonText className="text-md text-info-700 underline data-[hover=true]:text-info-800">
                      See how it works
                    </ButtonText>
                  </Button>
                </HStack>
              </VStack>
              <DirectoryModFilters
                {...filterProps}
                productTypeId={productTypeId}
                setProductTypeId={selectProductType}
                selectedProductTypeLabel={selectedProductTypeLabel}
              />
              {isWeb ? (
                <TrackedRouterLink
                  href="/inquiry"
                  asChild
                  trackingEvent={ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED}
                  trackingData={{ source: 'directory_mobile_header' }}>
                  <Button size="sm" variant="solid">
                    <Icon as={SquarePen} className="text-typography-0" />
                    <ButtonText>{NAV_LABELS.inquiry.webNav}</ButtonText>
                  </Button>
                </TrackedRouterLink>
              ) : null}
            </VStack>
          )
        }
        data={data}
        keyExtractor={directoryKeyExtractor}
        removeClippedSubviews={!isWeb}
        maxToRenderPerBatch={FLATLIST_MAX_RENDER_BATCH}
        windowSize={FLATLIST_WINDOW_SIZE}
        renderItem={renderDirectoryItem}
        onEndReachedThreshold={PAGINATION_THRESHOLD}
        onEndReached={fetchNextDirectoryPage}
        ListEmptyComponent={renderEmptyState(numColumns)}
        ListFooterComponent={
          <VStack className="mb-4">{isFetchingNextPage ? <Spinner size="large" /> : null}</VStack>
        }
      />
    );
  };

  return (
    <>
      {/* Auto onboarding tutorial modal for new users */}
      {withOnboardingTutorial && (
        <OnboardingTutorialModal
          isOpen={showNewUserDemoModal}
          onClose={() => setShowNewUserDemoModal(false)}
          onComplete={() => setShowNewUserDemoModal(false)}
          markShown={markShown}
          markShowLater={markShowLater}
          steps={onboardingSteps}
          userType={currentUser?.userType}
        />
      )}

      {/* Manual demo modal from "See how it works" */}
      <WhenToUseExplainer
        isOpen={isExplainerEnabled && isTreatmentSupportExplainerOpen}
        surface="section"
        isNewBadgeVisible={showTreatmentSupportNewBadge}
        team={getProductSupportTeam(String(productSupportProfiles[0]?.id ?? ''))}
        onClose={() => setIsTreatmentSupportExplainerOpen(false)}
        onAskTeam={() => {
          setIsTreatmentSupportExplainerOpen(false);

          navigate(`/medical-on-demand/inquiries/${productSupportProfiles[0]?.id}` as Href);
        }}
        onAskExpert={() => {
          setIsTreatmentSupportExplainerOpen(false);

          navigate(ROUTES.INQUIRY);
        }}
      />

      <InquiryDemoModal
        isOpen={showDemoModalManual}
        onClose={() => setShowDemoModalManual(false)}
        mode="manual"
      />

      {/* Guidelines modal/actionsheet removed from directory */}

      {/* The tagline in the band below is the page's heading from md up. Below md the band is
          hidden, so this names the page for screen readers and for focus after a navigation. */}
      {isWeb ? (
        <Heading size="h1-lg" className="sr-only md:hidden">
          {NAV_LABELS.directory.webNav}
        </Heading>
      ) : null}

      <VStack className="hidden bg-primary-500 md:flex" style={{ zIndex: 1 }}>
        <VStack className="mx-auto w-full max-w-screen-xl flex-1 px-5 md:px-10">
          {/* The band's vertical padding is animated rather than a class, so it
              tightens with the prose instead of leaving hero-sized space around
              the controls. */}
          <Animated.View style={bandStyle}>
            <Animated.View style={introStyle}>
              {/* React Native's own View, because the measurement rides on
                  `onLayout` and the web VStack renders a bare div that drops it.
                  Padding rather than a margin, so the gap above the filters is
                  measured with the prose and collapses with it. */}
              <View className="pb-4" onLayout={onIntroLayout}>
                <Text size="2xl" className="mb-2 text-typography-0" role="heading" aria-level={1}>
                  {directoryTagline}
                </Text>
                <Text className="text-md leading-normal text-typography-0">
                  {directoryDescription}{' '}
                  <LinkText
                    size="md"
                    onPress={() => setShowDemoModalManual(true)}
                    className="decoration-typography-0/60 text-typography-0 underline data-[hover=true]:text-typography-0">
                    See how it works
                  </LinkText>
                </Text>
              </View>
            </Animated.View>

            <HStack className="flex-row gap-3">
              <DirectoryModFilters
                {...filterProps}
                productTypeId={productTypeId}
                setProductTypeId={selectProductType}
                selectedProductTypeLabel={selectedProductTypeLabel}
              />
            </HStack>
          </Animated.View>
        </VStack>
      </VStack>

      {/*
        The phone layout takes no side padding here: its lists pad their own rows, so the
        banner and the segment control at the top of each list can run edge to edge.
      */}
      <VStack
        className={twJoin(
          'mx-auto w-full max-w-screen-xl flex-1',
          !useMobileTabLayout && 'px-5 md:px-10'
        )}>
        {!isWeb ? (
          <Header
            title="Ask an expert"
            isLarge
            contentScrollsUnderHeader={useMobileTabLayout}
            rightItems={TAB_HEADER_ITEMS}
          />
        ) : (
          <Header headerShown={false} title="Ask an expert your question." isLarge />
        )}

        {announcementBanner && !useMobileTabLayout ? (
          <VStack className="-mx-5 -mt-1 md:-mx-10">{announcementBanner}</VStack>
        ) : null}

        {renderDirectoryMainContent()}

        <ResultsAnnouncer message={resultsAnnouncement} />
      </VStack>
    </>
  );
}
